import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/app-layout";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Circle,
  CircleDot,
  Building2,
  BedDouble,
  DollarSign,
  Users,
  Lock,
  ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
type SetupStepKey = "property" | "rooms" | "pricing" | "team" | "devices";

interface SetupStepStatus {
  key: SetupStepKey;
  done: boolean;
  current: number;
  total: number;
  label: string | null;
}

interface SetupStatusResponse {
  steps: SetupStepStatus[];
  completed: number;
  total: number;
  percent: number;
}

interface StepDisplay {
  key: SetupStepKey;
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  cta: string;
  pendingCta?: string;
}

const STEP_DISPLAY: Record<SetupStepKey, StepDisplay> = {
  property: {
    key: "property",
    title: "Add your first property",
    description: "Create a property record so you can start managing rooms, pricing, and members.",
    icon: Building2,
    href: "/app/properties",
    cta: "Go to properties",
  },
  rooms: {
    key: "rooms",
    title: "Set up rooms on every property",
    description: "Every property needs at least one room before you can list it or assign members.",
    icon: BedDouble,
    href: "/app/properties",
    cta: "Manage rooms",
  },
  pricing: {
    key: "pricing",
    title: "Assign a pricing model to every room",
    description: "Create pricing models and assign one to each room so listings price correctly.",
    icon: DollarSign,
    href: "/app/pricing",
    cta: "Assign pricing",
    pendingCta: "Configure pricing",
  },
  team: {
    key: "team",
    title: "Invite a teammate",
    description: "Add at least one teammate and have them accept the invite so they can help run your portfolio.",
    icon: Users,
    href: "/app/team",
    cta: "Manage team",
    pendingCta: "Invite teammates",
  },
  devices: {
    key: "devices",
    title: "Connect a smart device",
    description: "Link a smart lock or thermostat to automate access and monitoring.",
    icon: Lock,
    href: "/app/devices",
    cta: "Connect a device",
  },
};

const STEP_ORDER: SetupStepKey[] = ["property", "rooms", "pricing", "team", "devices"];

export default function AppSetup() {
  usePageTitle("Setup");
  const statusQuery = useQuery<SetupStatusResponse>({
    queryKey: ["/api/app/setup-status"],
  });

  const isLoading = statusQuery.isLoading;
  const data = statusQuery.data;

  const steps = data?.steps ?? [];
  const stepsByKey = new Map(steps.map((s) => [s.key, s]));
  const completed = data?.completed ?? 0;
  const total = data?.total ?? STEP_ORDER.length;
  const percent = data?.percent ?? 0;

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="page-app-setup">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold" data-testid="text-setup-title" data-tour="setup-heading">
            Set Up
          </h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="setup" />
            <GuideButton pageKey="setup" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="setup" />

        <Card className="mb-6" data-tour="setup-progress" data-testid="card-setup-progress">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-muted-foreground">Setup progress</p>
                <p className="text-2xl font-semibold" data-testid="text-setup-progress-count">
                  {isLoading ? "—" : `${completed} of ${total} complete`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-primary" data-testid="text-setup-progress-percent">
                  {isLoading ? "—" : `${percent}%`}
                </p>
              </div>
            </div>
            <Progress value={isLoading ? 0 : percent} data-testid="progress-setup" />
            {!isLoading && total > 0 && completed === total && (
              <p className="mt-3 text-sm text-green-600 dark:text-green-400" data-testid="text-setup-complete">
                You're all set — every onboarding step is complete.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3" data-tour="setup-checklist" data-testid="list-setup-checklist">
          {isLoading
            ? Array.from({ length: STEP_ORDER.length }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" data-testid={`skeleton-setup-step-${i}`} />
              ))
            : STEP_ORDER.map((key) => {
                const display = STEP_DISPLAY[key];
                const status = stepsByKey.get(key);
                if (!status) return null;
                const Icon = display.icon;
                const isPartial =
                  !status.done &&
                  status.total > 0 &&
                  status.current > 0 &&
                  status.current < status.total;
                const cta =
                  !status.done && status.current === 0 && display.pendingCta
                    ? display.pendingCta
                    : display.cta;
                return (
                  <Card
                    key={key}
                    className={status.done ? "bg-muted/40" : ""}
                    data-testid={`card-setup-step-${key}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="shrink-0">
                        {status.done ? (
                          <CheckCircle2
                            className="w-7 h-7 text-green-600 dark:text-green-400"
                            data-testid={`icon-setup-step-${key}-done`}
                          />
                        ) : isPartial ? (
                          <CircleDot
                            className="w-7 h-7 text-amber-500 dark:text-amber-400"
                            data-testid={`icon-setup-step-${key}-partial`}
                          />
                        ) : (
                          <Circle
                            className="w-7 h-7 text-muted-foreground"
                            data-testid={`icon-setup-step-${key}-pending`}
                          />
                        )}
                      </div>
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p
                            className={`font-medium ${status.done ? "line-through text-muted-foreground" : ""}`}
                            data-testid={`text-setup-step-${key}-title`}
                          >
                            {display.title}
                          </p>
                          {isPartial && (
                            <Badge
                              variant="outline"
                              className="border-amber-500/50 text-amber-700 dark:text-amber-400"
                              data-testid={`badge-setup-step-${key}-progress`}
                            >
                              {status.current}/{status.total}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{display.description}</p>
                        {status.label && (
                          <p
                            className={`text-xs mt-1 ${
                              status.done
                                ? "text-muted-foreground"
                                : isPartial
                                ? "text-amber-700 dark:text-amber-400"
                                : "text-muted-foreground"
                            }`}
                            data-testid={`text-setup-step-${key}-status`}
                          >
                            {status.label}
                          </p>
                        )}
                      </div>
                      <Link href={display.href}>
                        <Button
                          variant={status.done ? "outline" : "default"}
                          size="sm"
                          data-testid={`button-setup-step-${key}`}
                        >
                          {status.done ? "Review" : cta}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
        </div>
      </div>
    </AppLayout>
  );
}
